import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class DeletePolicyCategoryParams {
  @IsUUID()
  @IsNotEmpty()
  category_id: string;
}

export class GetPolicyAttachedFileParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  attached_file_id: string;
}

export class MarkPolicyVersionAsReadParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class GetEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class CreatePolicyVersionParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;
}

export class PolicyNotAcceptedParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class PolicyAcceptedParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class CreatePolicyAcceptedZipParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class PublishPolicyVersionParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class UpdatePolicyVersionParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class DeletePolicyParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class GetPolicyVersionFileHashParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class GetPolicyVersionAcquaintanceFileHashParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class GetPolicyAcquaintanceFileParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class GetPolicyVersionFileParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class UpdatePolicyParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;
}

export class GetPolicyWithVersionsParams {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;
}

export class GetCompanyPositionsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetPoliciesCategoriesFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetPolicyCategoryListQuery {
  @IsBoolean()
  @IsOptional()
  hierarchic_view?: boolean;

  @IsUUID()
  @IsOptional()
  hierarchic_start_item_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company_ids?: string[];

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class CreatePolicyAcceptedZipQuery {
  @IsUUID()
  @IsOptional()
  employee_id?: string;
}

export class GetPolicyVersionAcquaintanceFileHashQuery {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetPolicyAcquaintanceFileQuery {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetCompaniesFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetPolicyPositionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetPolicyCompaniesQuery {
  @IsUUID()
  @IsOptional()
  head_company_id?: string;

  @IsBoolean()
  @IsOptional()
  head_companies_only?: boolean;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export type PolicyCategoryAccountOptionsResponse = FilterSelectOption[];

export type PolicyCategoryOptionsResponse = FilterSelectOption[];

export class PolicyCategoryItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  path: string[];

  @IsBoolean()
  @IsNotEmpty()
  have_children: boolean;

  @ValidateNested({ each: true })
  @Type(() => PolicyCategoryItem)
  @IsArray()
  @IsNotEmpty()
  children: PolicyCategoryItem[];

  @IsInt()
  @IsOptional()
  policies_amount?: number;

  @IsString()
  @IsNotEmpty()
  subtext: string;
}

export class GetPolicyCategoryListResponse {
  @ValidateNested({ each: true })
  @Type(() => PolicyCategoryItem)
  @IsArray()
  @IsNotEmpty()
  categories: PolicyCategoryItem[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class CreatePolicyCategoryResponse {
  @IsUUID()
  @IsNotEmpty()
  id: string;
}

export class AcceptPoliciesResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  need_confirm: string[];
}

export class PolicyEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  full_name: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @IsIn(['after_expiry'])
  @IsString()
  @IsOptional()
  caption?: 'after_expiry';

  @IsDateString()
  @IsOptional()
  dismissed_at?: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsBoolean()
  @IsOptional()
  is_expired?: boolean;
}

export class GetPolicyEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => PolicyEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: PolicyEmployee[];
}

export class CreatePolicyVersionResponse {
  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;
}

export class PolicyAcceptedZipResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class PolicyFileHash {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  hash: string;
}

export class GetPolicyFileHashesResponse {
  @ValidateNested({ each: true })
  @Type(() => PolicyFileHash)
  @IsArray()
  @IsNotEmpty()
  hashes: PolicyFileHash[];
}

export class PolicyFileHashResponse {
  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  hash: string;
}

export type PolicyAvailableFiltersNamesOptionsResponse = FilterSelectOption[];

export class GetGovDocumentTypeOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];
}

export class ParentPolicy {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsIn(['draft', 'past', 'actual', 'future'])
  @IsString()
  @IsNotEmpty()
  version_status: 'draft' | 'past' | 'actual' | 'future';

  @IsDateString()
  @IsOptional()
  published_at?: string;
}

export class ChildCompaniesPoliciesItem {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsIn(['draft', 'past', 'actual', 'future'])
  @IsString()
  @IsNotEmpty()
  version_status: 'draft' | 'past' | 'actual' | 'future';

  @IsDateString()
  @IsOptional()
  published_at?: string;
}

export class PolicyVersion {
  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;

  @IsDateString()
  @IsNotEmpty()
  active_from: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @IsInt()
  @IsNotEmpty()
  accepted_employees: number;

  @IsInt()
  @IsNotEmpty()
  total_employees: number;

  @IsDateString()
  @IsOptional()
  document_date?: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @IsIn(['pep', 'unep', 'unep_disabled', 'ukep'])
  @IsString()
  @IsNotEmpty()
  signature_type: 'pep' | 'unep' | 'unep_disabled' | 'ukep';

  @IsBoolean()
  @IsNotEmpty()
  with_order: boolean;

  @IsInt()
  @IsOptional()
  acceptance_period_days?: number;

  @IsDateString()
  @IsOptional()
  published_at?: string;

  @IsIn(['draft', 'past', 'actual', 'future'])
  @IsString()
  @IsNotEmpty()
  version_status: 'draft' | 'past' | 'actual' | 'future';
}

export class PolicyUnit {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export type CryptoProPluginOpenType = string;

export class PolicyCompany {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';

  @IsBoolean()
  @IsNotEmpty()
  employee_download_pdf_access: boolean;

  @IsBoolean()
  @IsOptional()
  use_acquaintance?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  has_child_companies: boolean;

  @IsUUID()
  @IsOptional()
  head_company_id?: string;
}

export class GetPolicyWithVersionsResponse {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => PolicyCompany)
  @IsNotEmpty()
  company: PolicyCompany;

  @ValidateNested({ each: true })
  @Type(() => PolicyUnit)
  @IsArray()
  @IsNotEmpty()
  units: PolicyUnit[];

  @IsBoolean()
  @IsOptional()
  units_deleted?: boolean;

  @IsString()
  @IsNotEmpty()
  unit_type: string;

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsBoolean()
  @IsOptional()
  positions_deleted?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsString()
  @IsOptional()
  gov_document_type_name?: string;

  @IsString()
  @IsOptional()
  gov_document_type_code?: string;

  @ValidateNested({ each: true })
  @Type(() => PolicyVersion)
  @IsArray()
  @IsNotEmpty()
  versions: PolicyVersion[];

  @IsBoolean()
  @IsOptional()
  hide_if_accepted?: boolean;

  @IsBoolean()
  @IsOptional()
  can_edit?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  attached_file_ids?: string[];

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsString()
  @IsOptional()
  category_name?: string;

  @ValidateNested({ each: true })
  @Type(() => ChildCompaniesPoliciesItem)
  @IsArray()
  @IsOptional()
  child_companies_policies?: ChildCompaniesPoliciesItem[];

  @IsIn(['parent', 'child'])
  @IsString()
  @IsOptional()
  hierarchy_type?: 'parent' | 'child';

  @ValidateNested()
  @Type(() => ParentPolicy)
  @IsOptional()
  parent_policy?: ParentPolicy;
}

export class CreatePolicyResponse {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;
}

export class PolicyXlsxByEmployeeFiltersResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class PolicyXlsxByEmployeeIdsResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class CompanyPolicyXlsxResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class KonturSystemDown {
  @IsBoolean()
  @IsNotEmpty()
  cert: boolean;

  @IsBoolean()
  @IsNotEmpty()
  sign: boolean;

  @IsString()
  @IsOptional()
  message?: string;
}

export class EmployeePolicyListItem {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => PolicyCompany)
  @IsNotEmpty()
  company: PolicyCompany;

  @IsIn(['not_signed', 'in_progress', 'signed', 'canceled', 'error'])
  @IsString()
  @IsNotEmpty()
  status: 'not_signed' | 'in_progress' | 'signed' | 'canceled' | 'error';

  @IsString()
  @IsNotEmpty()
  employee_id: string;

  @IsIn([
    'pep',
    'unep_disabled',
    'unep_kontur',
    'unep_goskey',
    'unep_cryptopro_simple',
    'unep_cryptopro_local',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  signature_type:
    | 'pep'
    | 'unep_disabled'
    | 'unep_kontur'
    | 'unep_goskey'
    | 'unep_cryptopro_simple'
    | 'unep_cryptopro_local'
    | 'ukep';

  @IsBoolean()
  @IsOptional()
  signature_type_substituted?: boolean;

  @ValidateNested()
  @Type(() => KonturSystemDown)
  @IsOptional()
  kontur_system_down?: KonturSystemDown;

  @IsDateString()
  @IsOptional()
  read_at?: string;

  @IsIn(['snils_not_found'])
  @IsString()
  @IsOptional()
  signature_detailed_error?: 'snils_not_found';

  @IsBoolean()
  @IsOptional()
  hide_if_accepted?: boolean;

  @IsBoolean()
  @IsOptional()
  can_edit?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  attached_file_ids?: string[];

  @IsInt()
  @IsOptional()
  acceptance_period_days?: number;

  @IsDateString()
  @IsOptional()
  accept_until?: string;

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsString()
  @IsOptional()
  category_name?: string;
}

export class EmployeePolicyListResponse {
  @ValidateNested({ each: true })
  @Type(() => EmployeePolicyListItem)
  @IsArray()
  @IsNotEmpty()
  policies: EmployeePolicyListItem[];
}

export class CompanyPolicyListItem {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => PolicyCompany)
  @IsNotEmpty()
  company: PolicyCompany;

  @IsDateString()
  @IsNotEmpty()
  active_from: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @IsString()
  @IsOptional()
  gov_document_type_name?: string;

  @IsString()
  @IsOptional()
  gov_document_type_code?: string;

  @IsInt()
  @IsNotEmpty()
  accepted_employees: number;

  @IsInt()
  @IsNotEmpty()
  total_employees: number;

  @IsIn(['pep', 'unep', 'ukep', 'unep_disabled'])
  @IsString()
  @IsNotEmpty()
  signature_type: 'pep' | 'unep' | 'ukep' | 'unep_disabled';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsBoolean()
  @IsOptional()
  hide_if_accepted?: boolean;

  @IsBoolean()
  @IsOptional()
  can_edit?: boolean;

  @IsInt()
  @IsOptional()
  acceptance_period_days?: number;

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsString()
  @IsOptional()
  category_name?: string;

  @IsDateString()
  @IsOptional()
  published_at?: string;

  @IsIn(['parent', 'child'])
  @IsString()
  @IsOptional()
  hierarchy_type?: 'parent' | 'child';

  @IsIn(['draft', 'past', 'actual', 'future'])
  @IsString()
  @IsNotEmpty()
  version_status: 'draft' | 'past' | 'actual' | 'future';

  @IsInt()
  @IsNotEmpty()
  child_policies_amount: number;
}

export class CompanyPolicyListResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyPolicyListItem)
  @IsArray()
  @IsNotEmpty()
  policies: CompanyPolicyListItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;

  @IsInt()
  @IsNotEmpty()
  total_version_count: number;

  @IsInt()
  @IsNotEmpty()
  total_unaccepted_count: number;

  @IsInt()
  @IsNotEmpty()
  total_unaccepted_version_count: number;

  @IsInt()
  @IsNotEmpty()
  unpublished_versions_count: number;
}

export class FilterTimeRange {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['timerange'])
  @IsString()
  @IsNotEmpty()
  type: 'timerange';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsIn(['till_now', 'any', 'from_now'])
  @IsString()
  @IsNotEmpty()
  allow_dates: 'till_now' | 'any' | 'from_now';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterBool {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['bool'])
  @IsString()
  @IsNotEmpty()
  type: 'bool';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsString()
  @IsOptional()
  tooltip?: string;
}

export class FilterMultipleSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple-search'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple-search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['search'])
  @IsString()
  @IsNotEmpty()
  type: 'search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterText {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterMultiple {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  clearable?: boolean;
}

export class FilterSelect {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['select'])
  @IsString()
  @IsNotEmpty()
  type: 'select';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class PolicyAvailableFiltersResponse {
  @IsArray()
  @IsNotEmpty()
  filters: (
    | FilterSelect
    | FilterMultiple
    | FilterText
    | FilterSearch
    | FilterMultipleSearch
    | FilterBool
    | FilterTimeRange
  )[];
}

export type PolicyPositionsOptionsResponse = FilterSelectOption[];

export type PolicyCompaniesPositionsResponse = FilterSelectOption[];

export class GetPolicyCompanies {
  @ValidateNested({ each: true })
  @Type(() => PolicyCompany)
  @IsArray()
  @IsNotEmpty()
  companies: PolicyCompany[];
}

export class CreatePolicyCategoryRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class ConfirmAcceptPoliciesRequest {
  @IsString()
  @IsNotEmpty()
  code: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  policy_versions: string[];
}

export class AcceptPoliciesRequestPolicy_versions {
  @IsUUID()
  @IsNotEmpty()
  policy_version_id: string;

  @IsUUID()
  @IsNotEmpty()
  employee_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  signature?: string;
}

export class AcceptPoliciesRequest {
  @ValidateNested({ each: true })
  @Type(() => AcceptPoliciesRequestPolicy_versions)
  @IsArray()
  @IsNotEmpty()
  policy_versions: AcceptPoliciesRequestPolicy_versions[];
}

export class GetPolicyEmployeesRequestFilters {
  @IsBoolean()
  @IsOptional()
  accepted?: boolean;

  @IsString()
  @IsOptional()
  employee_query?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  policy_version_id?: string[];
}

export class GetPolicyEmployeesRequestPagination {
  @IsInt()
  @IsOptional()
  offset?: number;

  @IsInt()
  @IsOptional()
  limit?: number;
}

export class GetPolicyEmployeesRequest {
  @ValidateNested()
  @Type(() => GetPolicyEmployeesRequestPagination)
  @IsNotEmpty()
  pagination: GetPolicyEmployeesRequestPagination;

  @ValidateNested()
  @Type(() => GetPolicyEmployeesRequestFilters)
  @IsNotEmpty()
  filters: GetPolicyEmployeesRequestFilters;
}

export class CreatePolicyVersionRequest {
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  files: string[];

  @IsDateString()
  @IsNotEmpty()
  active_from: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @IsDateString()
  @IsNotEmpty()
  document_date: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @IsIn(['pep', 'unep', 'ukep'])
  @IsString()
  @IsNotEmpty()
  signature_type: 'pep' | 'unep' | 'ukep';

  @IsBoolean()
  @IsNotEmpty()
  with_order: boolean;

  @IsInt()
  @IsOptional()
  acceptance_period_days?: number;
}

export class UpdatePolicyVersionRequest {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  files?: string[];

  @IsDateString()
  @IsNotEmpty()
  active_from: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @IsDateString()
  @IsNotEmpty()
  document_date: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @IsIn(['pep', 'unep', 'ukep'])
  @IsString()
  @IsNotEmpty()
  signature_type: 'pep' | 'unep' | 'ukep';

  @IsBoolean()
  @IsNotEmpty()
  with_order: boolean;
}

export class GetPolicyFileHashesRequestHashes {
  @IsUUID()
  @IsNotEmpty()
  policy_id: string;
}

export class GetPolicyFileHashesRequest {
  @ValidateNested({ each: true })
  @Type(() => GetPolicyFileHashesRequestHashes)
  @IsArray()
  @IsNotEmpty()
  hashes: GetPolicyFileHashesRequestHashes[];
}

export class UpdatePolicyRequest {
  @IsBoolean()
  @IsNotEmpty()
  hide_if_accepted: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsString()
  @IsOptional()
  unit_type?: string;

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsArray()
  @IsOptional()
  employee_ids?: string[][];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  attached_file_ids?: string[];

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  create_for_child_companies?: string[];
}

export class CreatePolicyRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  files: string[];

  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsDateString()
  @IsNotEmpty()
  active_from: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @IsInt()
  @IsOptional()
  acceptance_period_days?: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsString()
  @IsOptional()
  unit_type?: string;

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;

  @IsDateString()
  @IsNotEmpty()
  document_date: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @IsIn(['pep', 'unep'])
  @IsString()
  @IsNotEmpty()
  signature_type: 'pep' | 'unep';

  @IsBoolean()
  @IsNotEmpty()
  with_order: boolean;

  @IsString()
  @IsOptional()
  gov_document_type_code?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsBoolean()
  @IsOptional()
  hide_if_accepted?: boolean;

  @IsArray()
  @IsOptional()
  employee_ids?: string[][];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  attached_file_ids?: string[];

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  create_for_child_companies?: string[];
}

export type SignType = string;

export class TimeRange {
  @IsDateString()
  @IsOptional()
  start?: string;

  @IsDateString()
  @IsOptional()
  end?: string;
}

export type EmployeeStatus = string;

export class EmployeeFilter {
  @IsString()
  @IsOptional()
  personnel_number_fio_query?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  company_ids?: string[];

  @IsUUID()
  @IsOptional()
  employee_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsIn(
    [
      'data_received',
      'documents_verification',
      'unep_application_signing',
      'unep_releasing',
      'unep_release_failed',
      'unep_release_paused',
      'invite_sent',
      'invite_expired',
      'active',
      'dismissed',
      'wait_registration',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  statuses?: (
    | 'data_received'
    | 'documents_verification'
    | 'unep_application_signing'
    | 'unep_releasing'
    | 'unep_release_failed'
    | 'unep_release_paused'
    | 'invite_sent'
    | 'invite_expired'
    | 'active'
    | 'dismissed'
    | 'wait_registration'
  )[];

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  status_changed_at?: TimeRange;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  employee_created_at?: TimeRange;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsBoolean()
  @IsOptional()
  include_subunits?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  segments?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  profiles?: string[];

  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsOptional()
  sign_type?:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsBoolean()
  @IsOptional()
  only_representative?: boolean;
}

export class PolicyXlsxByEmployeeFiltersRequest {
  @ValidateNested()
  @Type(() => EmployeeFilter)
  @IsNotEmpty()
  filter: EmployeeFilter;
}

export class PolicyXlsxByEmployeeIdsRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsBoolean()
  @IsOptional()
  with_files?: boolean;
}

export class CompanyPolicyFilter {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  names?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsBoolean()
  @IsOptional()
  include_subunits?: boolean;

  @IsUUID()
  @IsOptional()
  category_id?: string;

  @IsIn(['pep', 'unep', 'ukep'])
  @IsString()
  @IsOptional()
  signature_type?: 'pep' | 'unep' | 'ukep';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsIn(['draft', 'past', 'actual', 'future'])
  @IsString()
  @IsOptional()
  version_status?: 'draft' | 'past' | 'actual' | 'future';

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  document_version_start_date?: TimeRange;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  document_version_end_date?: TimeRange;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  document_date?: TimeRange;

  @IsBoolean()
  @IsOptional()
  exclude_common_company?: boolean;

  @IsBoolean()
  @IsOptional()
  include_parent_policies?: boolean;
}

export class CompanyPolicyXlsxRequest {
  @ValidateNested()
  @Type(() => CompanyPolicyFilter)
  @IsOptional()
  filters?: CompanyPolicyFilter;

  @IsBoolean()
  @IsOptional()
  only_not_accepted_employees?: boolean;

  @IsUUID()
  @IsOptional()
  policy_id?: string;
}

export class EmployeePolicyListRequestFilters {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  policy_version_ids?: string[];
}

export class EmployeePolicyListRequestPagination {
  @IsInt()
  @IsOptional()
  offset?: number;

  @IsInt()
  @IsOptional()
  limit?: number;
}

export class EmployeePolicyListRequest {
  @ValidateNested()
  @Type(() => EmployeePolicyListRequestPagination)
  @IsNotEmpty()
  pagination: EmployeePolicyListRequestPagination;

  @ValidateNested()
  @Type(() => EmployeePolicyListRequestFilters)
  @IsNotEmpty()
  filters: EmployeePolicyListRequestFilters;
}

export class CompanyPolicyListRequestPagination {
  @IsInt()
  @IsOptional()
  offset?: number;

  @IsInt()
  @IsOptional()
  limit?: number;
}

export class CompanyPolicyListRequest {
  @ValidateNested()
  @Type(() => CompanyPolicyListRequestPagination)
  @IsNotEmpty()
  pagination: CompanyPolicyListRequestPagination;

  @ValidateNested()
  @Type(() => CompanyPolicyFilter)
  @IsNotEmpty()
  filters: CompanyPolicyFilter;
}

export class GetPolicyCompanyPositionsRequestFilters {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;
}

export class Pagination {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetPolicyCompanyPositionsRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => GetPolicyCompanyPositionsRequestFilters)
  @IsOptional()
  filters?: GetPolicyCompanyPositionsRequestFilters;
}

@injectable()
export class PolicyService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getCategoryAccountOptions() {
    let requestPath = '/policy/category/account/options';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getPoliciesCategoriesFilterOptions({
    query = {},
  }: {
    query?: GetPoliciesCategoriesFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetPoliciesCategoriesFilterOptionsQuery,
      ),
    ]);

    let requestPath = '/policy/category/options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getPolicyCategoryList({
    query = {},
  }: {
    query?: GetPolicyCategoryListQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetPolicyCategoryListQuery),
    ]);

    let requestPath = '/policy/category/list';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetPolicyCategoryListResponse,
    );

    return response;
  }

  async deletePolicyCategory({
    params,
  }: {
    params: DeletePolicyCategoryParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeletePolicyCategoryParams),
    ]);

    let requestPath = '/policy/category/{category_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeletePolicyCategoryParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async createPolicyCategory({ body }: { body: CreatePolicyCategoryRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreatePolicyCategoryRequest),
    ]);

    let requestPath = '/policy/category';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreatePolicyCategoryResponse,
    );

    return response;
  }

  async getPolicyAttachedFile({
    params,
  }: {
    params: GetPolicyAttachedFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetPolicyAttachedFileParams),
    ]);

    let requestPath = '/policy/{policy_id}/attached/{attached_file_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyAttachedFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async acceptConfirmPolicies({
    body,
  }: {
    body: ConfirmAcceptPoliciesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, ConfirmAcceptPoliciesRequest),
    ]);

    let requestPath = '/policy/confirm_accept';

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async acceptPolicies({ body }: { body: AcceptPoliciesRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, AcceptPoliciesRequest),
    ]);

    let requestPath = '/policy/accept';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AcceptPoliciesResponse,
    );

    return response;
  }

  async markPolicyVersionAsRead({
    params,
  }: {
    params: MarkPolicyVersionAsReadParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, MarkPolicyVersionAsReadParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/version/{policy_version_id}/mark_read';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof MarkPolicyVersionAsReadParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async getEmployees({
    params,
    body,
  }: {
    params: GetEmployeesParams;
    body: GetPolicyEmployeesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEmployeesParams),
      this.validator.validateOrReject(body, GetPolicyEmployeesRequest),
    ]);

    let requestPath =
      '/policy/{policy_id}/version/{policy_version_id}/employees';

    const paramsKeys = Object.keys(params) as (keyof GetEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetPolicyEmployeesResponse,
    );

    return response;
  }

  async createPolicyVersion({
    params,
    body,
  }: {
    params: CreatePolicyVersionParams;
    body: CreatePolicyVersionRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CreatePolicyVersionParams),
      this.validator.validateOrReject(body, CreatePolicyVersionRequest),
    ]);

    let requestPath = '/policy/{policy_id}/version';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CreatePolicyVersionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreatePolicyVersionResponse,
    );

    return response;
  }

  async policyNotAccepted({ params }: { params: PolicyNotAcceptedParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, PolicyNotAcceptedParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/document/{policy_version_id}/not_accepted';

    const paramsKeys = Object.keys(params) as (keyof PolicyNotAcceptedParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async policyAccepted({ params }: { params: PolicyAcceptedParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, PolicyAcceptedParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/document/{policy_version_id}/accepted';

    const paramsKeys = Object.keys(params) as (keyof PolicyAcceptedParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async createPolicyAcceptedZip({
    query = {},
    params,
  }: {
    query?: CreatePolicyAcceptedZipQuery;
    params: CreatePolicyAcceptedZipParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, CreatePolicyAcceptedZipQuery),
      this.validator.validateOrReject(params, CreatePolicyAcceptedZipParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/document/{policy_version_id}/accepted/zip';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CreatePolicyAcceptedZipParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    requestPath = `${requestPath}${
      query && Object.keys(query).length
        ? `?${this.query.stringify(query)}`
        : ''
    }`;
    const result = await this.http.post(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      PolicyAcceptedZipResponse,
    );

    return response;
  }

  async publishPolicyVersion({
    params,
  }: {
    params: PublishPolicyVersionParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, PublishPolicyVersionParams),
    ]);

    let requestPath = '/policy/{policy_id}/version/{policy_version_id}/publish';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof PublishPolicyVersionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, {}, { withSide: true });

    return result;
  }

  async updatePolicyVersion({
    params,
    body,
  }: {
    params: UpdatePolicyVersionParams;
    body: UpdatePolicyVersionRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdatePolicyVersionParams),
      this.validator.validateOrReject(body, UpdatePolicyVersionRequest),
    ]);

    let requestPath = '/policy/{policy_id}/version/{policy_version_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdatePolicyVersionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async deletePolicy({ params }: { params: DeletePolicyParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeletePolicyParams),
    ]);

    let requestPath = '/policy/{policy_id}/version/{policy_version_id}';

    const paramsKeys = Object.keys(params) as (keyof DeletePolicyParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async getPolicyFileHashes({ body }: { body: GetPolicyFileHashesRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, GetPolicyFileHashesRequest),
    ]);

    let requestPath = '/policy/batch/hash';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetPolicyFileHashesResponse,
    );

    return response;
  }

  async getPolicyVersionFileHash({
    params,
  }: {
    params: GetPolicyVersionFileHashParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetPolicyVersionFileHashParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/version/{policy_version_id}/file/hash';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyVersionFileHashParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      PolicyFileHashResponse,
    );

    return response;
  }

  async getPolicyVersionAcquaintanceFileHash({
    query,
    params,
  }: {
    query: GetPolicyVersionAcquaintanceFileHashQuery;
    params: GetPolicyVersionAcquaintanceFileHashParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetPolicyVersionAcquaintanceFileHashQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetPolicyVersionAcquaintanceFileHashParams,
      ),
    ]);

    let requestPath =
      '/policy/{policy_id}/version/{policy_version_id}/acquaintance/file/hash';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyVersionAcquaintanceFileHashParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      PolicyFileHashResponse,
    );

    return response;
  }

  async getPolicyAcquaintanceFile({
    query,
    params,
  }: {
    query: GetPolicyAcquaintanceFileQuery;
    params: GetPolicyAcquaintanceFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetPolicyAcquaintanceFileQuery),
      this.validator.validateOrReject(params, GetPolicyAcquaintanceFileParams),
    ]);

    let requestPath =
      '/policy/{policy_id}/version/{policy_version_id}/acquaintance/file';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyAcquaintanceFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: true,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async getPolicyVersionFile({
    params,
  }: {
    params: GetPolicyVersionFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetPolicyVersionFileParams),
    ]);

    let requestPath = '/policy/{policy_id}/version/{policy_version_id}/file';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyVersionFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async getCompaniesFilterOptions({
    query = {},
  }: {
    query?: GetCompaniesFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCompaniesFilterOptionsQuery),
    ]);

    let requestPath = '/policy/available_filters/names_options';

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getGovDocumentTypeOptions() {
    let requestPath = '/policy/gov_document_type/options';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetGovDocumentTypeOptionsResponse,
    );

    return response;
  }

  async updatePolicy({
    params,
    body,
  }: {
    params: UpdatePolicyParams;
    body: UpdatePolicyRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdatePolicyParams),
      this.validator.validateOrReject(body, UpdatePolicyRequest),
    ]);

    let requestPath = '/policy/{policy_id}';

    const paramsKeys = Object.keys(params) as (keyof UpdatePolicyParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async getPolicyWithVersions({
    params,
  }: {
    params: GetPolicyWithVersionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetPolicyWithVersionsParams),
    ]);

    let requestPath = '/policy/{policy_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetPolicyWithVersionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetPolicyWithVersionsResponse,
    );

    return response;
  }

  async createPolicy({ body }: { body: CreatePolicyRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreatePolicyRequest),
    ]);

    let requestPath = '/policy';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreatePolicyResponse,
    );

    return response;
  }

  async policyXlsxByEmployeeFilters({
    body,
  }: {
    body: PolicyXlsxByEmployeeFiltersRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, PolicyXlsxByEmployeeFiltersRequest),
    ]);

    let requestPath = '/policy/xlsx/by_employee_filters';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      PolicyXlsxByEmployeeFiltersResponse,
    );

    return response;
  }

  async policyXlsxByEmployeeIds({
    body = {},
  }: {
    body?: PolicyXlsxByEmployeeIdsRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, PolicyXlsxByEmployeeIdsRequest),
    ]);

    let requestPath = '/policy/xlsx/by_employee_ids';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      PolicyXlsxByEmployeeIdsResponse,
    );

    return response;
  }

  async policyCompanyXlsx({
    body = {},
  }: {
    body?: CompanyPolicyXlsxRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, CompanyPolicyXlsxRequest),
    ]);

    let requestPath = '/policy/company/xlsx';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyPolicyXlsxResponse,
    );

    return response;
  }

  async getPolicyEmployeeList({ body }: { body: EmployeePolicyListRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, EmployeePolicyListRequest),
    ]);

    let requestPath = '/policy/employee/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      EmployeePolicyListResponse,
    );

    return response;
  }

  async getPolicyCompanyList({ body }: { body: CompanyPolicyListRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CompanyPolicyListRequest),
    ]);

    let requestPath = '/policy/company/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyPolicyListResponse,
    );

    return response;
  }

  async getPolicyAvailableFilters() {
    let requestPath = '/policy/list/available_filters';

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async getPolicyPositions({
    query = {},
  }: {
    query?: GetPolicyPositionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetPolicyPositionsQuery),
    ]);

    let requestPath = '/policy/positions_options';

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getCompanyPositions({
    params,
    body = {},
  }: {
    params: GetCompanyPositionsParams;
    body?: GetPolicyCompanyPositionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyPositionsParams),
      this.validator.validateOrReject(body, GetPolicyCompanyPositionsRequest),
    ]);

    let requestPath = '/policy/companies/{company_id}/positions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyPositionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getPolicyCompanies({
    query = {},
  }: {
    query?: GetPolicyCompaniesQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetPolicyCompaniesQuery),
    ]);

    let requestPath = '/policy/companies';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetPolicyCompanies,
    );

    return response;
  }
}
